import { useEffect, useState } from "react";
import { Bell, BellOff, X } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  iosNeedsInstall,
  currentSubscription,
  subscribePush,
  optedOut,
  setOptedOut,
} from "../lib/push";

const DEFER_KEY = "ass-push-defer";

/**
 * Auto-shown banner on first login asking to enable push notifications.
 * Hidden if:
 *  - user already subscribed
 *  - user previously dismissed (snoozed for 7 days) or opted out
 *  - push not supported
 *  - iOS Safari not installed as PWA (falls back to install banner)
 */
export default function PushBanner() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isPushSupported()) return;
      if (iosNeedsInstall()) return; // let the /install path handle iOS
      if (optedOut()) return;
      const defer = Number(localStorage.getItem(DEFER_KEY) || "0");
      if (defer && Date.now() < defer) return;
      // Only prompt if permission is still "default" (not granted, not denied)
      if (Notification.permission !== "default") return;
      const sub = await currentSubscription();
      if (sub) return;
      const t = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(t);
    })();
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      toast.success("Notifications activées");
      setVisible(false);
    } catch (e) {
      toast.error(e?.message || "Impossible d'activer les notifications");
      if ((e?.message || "").includes("Autorisation")) {
        // permission denied -> snooze forever until reset from browser
        setOptedOut(true);
        setVisible(false);
      }
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DEFER_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="push-banner"
      className="fixed inset-x-3 bottom-3 z-40 rounded-2xl border border-[#E5B85C]/50 bg-[#111827] shadow-2xl p-4 flex gap-3 items-center"
    >
      <div className="shrink-0 w-10 h-10 rounded-lg bg-[#E5B85C] flex items-center justify-center">
        <Bell className="w-5 h-5 text-[#0B0F17]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-white text-sm">
          Active les notifications
        </div>
        <div className="text-xs text-slate-400">
          Nouveaux matchs, rappels J-3 / J-1 et résultats en direct.
        </div>
      </div>
      <button
        data-testid="push-banner-enable"
        disabled={busy}
        onClick={enable}
        className="shrink-0 px-3 py-2 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] text-xs font-bold uppercase tracking-wide disabled:opacity-60"
      >
        {busy ? "…" : "Activer"}
      </button>
      <button
        data-testid="push-banner-dismiss"
        onClick={dismiss}
        className="shrink-0 p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-800"
        aria-label="Plus tard"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/** Compact icon-only fallback for header (optional). */
export function PushDeniedHint() {
  if (!isPushSupported()) return null;
  if (Notification.permission !== "denied") return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
      <BellOff className="w-3 h-3" /> notifications bloquées par le navigateur
    </span>
  );
}
